<template>
  <li class="text-sm leading-[1.8] w-full cursor-pointer" @click="goToPage">
    <div
      class="group chat-row flex items-center justify-between gap-1 rounded-xl p-3"
      :class="{ 'active-chat': isActive }"
    >
      <!-- Chat titel / rename input -->
      <div
        class="block flex-1 min-w-0 pr-2"
        @click="isRenaming ? $event.stopPropagation() : null"
      >
        <template v-if="isRenaming">
          <input
            ref="renameInputEl"
            v-model="draftTitle"
            maxlength="255"
            class="w-full bg-transparent border border-[var(--dropdown-border)] rounded-lg px-2 py-1 text-sm outline-none"
            @keydown.enter.prevent="confirmRename"
            @keydown.esc.prevent="cancelRename"
            @blur="confirmRename"
          />
        </template>

        <template v-else>
          <p class="truncate whitespace-nowrap overflow-hidden">
            {{ chatPreview.title }}
          </p>
        </template>
      </div>

      <!-- Ellipsis / menu knop -->
      <div class="relative">
        <button
          type="button"
          class="flex h-7 w-7 items-center justify-center rounded-md opacity-30 active:opacity-100 md:opacity-0 md:group-hover:opacity-100 menu-trigger"
          @click.stop="toggleMenu"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="6" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="18" cy="12" r="2" />
          </svg>
        </button>

        <!-- Dropdown -->
        <transition name="fade-scale">
          <div
            v-if="isOpen"
            ref="menuEl"
            class="menu-panel absolute right-0 top-full mt-2 w-52 rounded-xl shadow-xl backdrop-blur z-30"
            tabindex="-1"
            @keydown.stop.prevent="onMenuKeydown"
          >
            <button
              type="button"
              class="flex w-full items-center gap-2 p-3 text-md rename-row"
              @click.stop="startRename"
            >
              <UiIcon name="lucide:pencil" size="md" />
              <span>Naam aanpassen</span>
            </button>

            <button
              type="button"
              class="flex w-full items-center gap-2 p-3 text-md text-red-500 delete-row"
              @click.stop="onDelete"
            >
              <UiIcon name="lucide:trash-2" size="md" />
              <span>Verwijderen</span>
            </button>
          </div>
        </transition>
      </div>
    </div>
  </li>
</template>

<script setup lang="ts">
const route = useRoute();
const isRenaming = ref(false);
const draftTitle = ref("");
const renameInputEl = ref<HTMLInputElement | null>(null);
const menuEl = ref<HTMLElement | null>(null);
const focusedIndex = ref(-1);

const props = defineProps<{
  chatPreview: Preview;
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: "toggleMenu", id: string): void;
  (e: "delete", id: string): void;
  (e: "rename", payload: { id: string; title: string }): void;
}>();

const isActive = computed(() => {
  // route.params.id kan string of array zijn
  const currentId = Array.isArray(route.params.id)
    ? route.params.id[0]
    : route.params.id;

  const propId = Array.isArray(props.chatPreview.id)
    ? props.chatPreview.id[0]
    : props.chatPreview.id;

  return currentId === propId;
});

const isOpen = ref(props.isOpen);

// using watch and not emiting on close because for some reason it lag when closing with emit
// opening with emit works fine though, so don't remove the watcher
watch(
  () => props.isOpen,
  (value) => {
    isOpen.value = value;
  },
  { immediate: true }
);

watch(
  () => isOpen.value,
  async (open) => {
    if (!open) return;

    // nog geen item geselecteerd
    focusedIndex.value = -1;

    await nextTick();

    // focus op container zodat @keydown werkt, maar zonder button highlight
    menuEl.value?.focus();
  }
);

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onMenuKeydown);
});

const toggleMenu = () => {
  emit("toggleMenu", props.chatPreview.id);
};

const onDelete = () => {
  isOpen.value = false;
  emit("delete", props.chatPreview.id);
};

const startRename = async () => {
  isOpen.value = false;
  isRenaming.value = true;

  draftTitle.value = props.chatPreview.title ?? "";

  await nextTick();
  renameInputEl.value?.focus();
  renameInputEl.value?.select();
};

const cancelRename = () => {
  isRenaming.value = false;
  draftTitle.value = "";
};

const confirmRename = async () => {
  if (!isRenaming.value) return;

  const newTitle = draftTitle.value.trim().slice(0, 255);
  const chatId = props.chatPreview.id;

  // als niets veranderd: gewoon sluiten
  if (newTitle === (props.chatPreview.title ?? "").trim()) {
    isRenaming.value = false;
    return;
  }

  isRenaming.value = false;

  // emit naar parent
  emit("rename", { id: chatId, title: newTitle });
};

const getMenuButtons = () => {
  const el = menuEl.value;
  if (!el) return [];
  return Array.from(el.querySelectorAll<HTMLButtonElement>("button"));
};

const focumdenuItem = (index: number) => {
  const items = getMenuButtons();
  if (!items.length) return;

  const clamped = Math.max(0, Math.min(items.length - 1, index));
  focusedIndex.value = clamped;
  items[clamped]?.focus();
};

const closeMenu = () => {
  isOpen.value = false;
  window.removeEventListener("keydown", onMenuKeydown);
};

const onMenuKeydown = (e: KeyboardEvent) => {
  if (!isOpen.value) return;

  if (e.key === "ArrowDown") {
    focumdenuItem(focusedIndex.value + 1);
  } else if (e.key === "ArrowUp") {
    focumdenuItem(focusedIndex.value - 1);
  } else if (e.key === "Enter") {
    const items = getMenuButtons();
    items[focusedIndex.value]?.click();
  } else if (e.key === "Escape") {
    isOpen.value = false;
  }
};

// Optioneel: menu sluiten bij klik buiten het item
onMounted(() => {
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    // alleen sluiten als je buiten deze chat-row klikt
    if (!target.closest(".chat-row")) {
      isOpen.value = false;
    }
  };

  window.addEventListener("click", handleClick);

  onBeforeUnmount(() => {
    window.removeEventListener("click", handleClick);
  });
});

const goToPage = () => {
  if (props.chatPreview.link) {
    navigateTo(props.chatPreview.link);
  }
};
</script>

<style scoped>
/* Fade + scale animatie voor het menu */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition:
    opacity 0.12s ease-out,
    transform 0.12s ease-out;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

/* Row hover: gebruik theme-variabelen */
.chat-row {
  transition: background-color 0.12s;
}

/* normale hover */
.chat-row:hover {
  background-color: var(--chat-row-hover);
}

/* active state */
.active-chat {
  background-color: var(--chat-active-bg);
}

/* als active ook gehoverd wordt: active kleur laten winnen */
.chat-row.active-chat:hover {
  background-color: var(--chat-row-hover);
}

/* menu trigger hover */
.menu-trigger:hover {
  background-color: var(--menu-trigger-hover);
}

/* dropdown panel */
.menu-panel {
  background-color: var(--dropdown-bg);
  border: 1px solid var(--dropdown-border);
  box-shadow: 0 18px 40px var(--dropdown-shadow);
}

/* delete button */
.delete-row {
  border-radius: 0.6rem;
  color: rgb(239 68 68);
  transition: background-color 0.12s ease-out;
}

.delete-row:hover {
  background-color: var(--dropdown-delete-hover);
}

.delete-row:focus {
  outline: none;
  background-color: var(--dropdown-delete-hover);
}

/* rename button */
.rename-row {
  border-radius: 0.6rem;
  transition: background-color 0.12s ease-out;
}

.rename-row:hover {
  background-color: var(--chat-row-hover);
}

.rename-row:focus {
  outline: none;
  background-color: var(--chat-row-hover);
}
</style>
